import * as Sentry from '@sentry/react';
import Invoice from '../../types/Invoice';

const PAID_ORDER_STATUSES = ['completed', 'paid'];

/** True when the invoice's order has been charged (Completed or Paid). */
export function isOrderPaid(invoice?: Invoice): boolean {
  const status = invoice?.invoiceDetails?.order?.status?.toLowerCase();
  return !!status && PAID_ORDER_STATUSES.includes(status);
}

// ProPay reports ordinary card declines ("Insufficient funds", "Do not honor",
// "Expired card", ...) with this prefix. Anything else from the gateway is a
// system failure (e.g. ImpersonateService timeouts, server unavailable).
const DECLINE_PREFIX = 'Transaction failed:';

export function getDeclineReason(message?: string): string | undefined {
  const trimmed = message?.trim();
  if (!trimmed?.startsWith(DECLINE_PREFIX)) return undefined;
  return trimmed.slice(DECLINE_PREFIX.length).trim() || 'Unknown';
}

/**
 * Report a payment failure posted by the payment iframe.
 *
 * Card declines are expected user outcomes, so they are grouped into a single
 * info-level issue with the reason as a tag (one issue, trendable by reason)
 * instead of one warning issue per decline reason. Gateway/system failures stay
 * at warning level with default (message-based) grouping so each stays visible.
 */
export function reportPaymentFailure(
  code: string,
  message: string | undefined,
  invoiceId: string
): void {
  const declineReason = code === 'RS_PROPAY_ERROR' ? getDeclineReason(message) : undefined;

  if (declineReason) {
    Sentry.captureMessage(`Card declined - ${declineReason}`, {
      level: 'info',
      fingerprint: ['propay-decline'],
      tags: { 'payment.decline_reason': declineReason, invoiceId },
    });
    return;
  }

  Sentry.captureMessage(`Warning - ${code}: Payment Failure Handled - ${message}`, {
    level: 'warning',
    tags: { 'payment.failure_code': code, invoiceId },
  });
}
